/**
 * 题目 V2 tokens 读取与回退（对齐后端 question-v2-token-payload）
 * - 开关关 / tokens 缺省或空数组 → 返回空，调用方走 legacy word_en / en
 * - is_original=1 分段 tokens 恒为空 → 使用父题顶层 tokens
 * - is_direct_display：人名/地名/数字/缩写/专有名词等固定展示（同标点/数词，不占输入槽）
 * - 输入槽 expectedWords：expectedWordsFromStatement → tokens > word_en > en
 */
import { USE_QUESTION_V2_TOKENS } from '@/config/questionV2Tokens'
import { mergeEnglishGameTokens } from '@/utils/clockAbbrevMerge'
import { splitPipeField } from '@/utils/splitPipeField'
import { tokensEqualForWordEnAlign } from '@/utils/wordEnAlign'

/** 与 ModePlay.tokenizeEnglish / answerAreaPhoneticAlign.tokenizeEnglishForGame 一致 */
function tokenizeEnglishForExpectedWords (text) {
  if (!text) return []
  const tokens = String(text).match(/([A-Za-z0-9'\-]+|[^A-Za-z0-9'\-\s])/g)
  return tokens ? tokens.map((t) => t.trim()).filter(Boolean) : []
}

/**
 * 将 word_en / tokens 槽位展开为输入 expectedWords（槽内多词如 "Good morning" 再分词）
 * @param {string[]} slots
 * @returns {string[]}
 */
function expandWordSlotsToExpectedWords (slots) {
  if (!Array.isArray(slots) || !slots.length) return []
  const out = []
  for (let i = 0; i < slots.length; i++) {
    const raw = String(slots[i] ?? '').trim()
    if (!raw) continue
    const parts = mergeEnglishGameTokens(tokenizeEnglishForExpectedWords(raw))
    if (parts.length) {
      for (let j = 0; j < parts.length; j++) out.push(parts[j])
    } else {
      out.push(raw)
    }
  }
  return out
}

/**
 * 有 tokens 时展开：is_direct_display 的 surface 保持原子（如 U.S.），禁止再拆成 U . S .
 * 否则 matchDirectDisplayAt / fixedDisplay 对不上，缩写会变成可输入槽。
 * @param {{ surface: string, is_direct_display?: boolean }[]} tokens
 * @returns {string[]}
 */
function expandQuestionTokensToExpectedWords (tokens) {
  if (!Array.isArray(tokens) || !tokens.length) return []
  const out = []
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]
    const raw = String(t?.surface ?? '').trim()
    if (!raw) continue
    if (t.is_direct_display) {
      out.push(raw)
      continue
    }
    const parts = mergeEnglishGameTokens(tokenizeEnglishForExpectedWords(raw))
    if (parts.length) {
      for (let j = 0; j < parts.length; j++) out.push(parts[j])
    } else {
      out.push(raw)
    }
  }
  return out
}

/**
 * 输入槽 expectedWords：tokens > word_en > en
 * （勿对整句 en 盲分词优先——破折号等会与 word_en/tokens 错位）
 * @param {object|null|undefined} statement
 * @param {{ enFallback?: string }} [opts]
 * @returns {string[]}
 */
export function expectedWordsFromStatement (statement, opts = {}) {
  if (!statement) {
    const fb = String(opts.enFallback || '').trim()
    return fb ? mergeEnglishGameTokens(tokenizeEnglishForExpectedWords(fb)) : []
  }
  const qTokens = resolveQuestionTokens(statement)
  if (qTokens.length) return expandQuestionTokensToExpectedWords(qTokens)

  const wordEn = String(statement.word_en || '').trim()
  if (wordEn) return expandWordSlotsToExpectedWords(splitPipeField(wordEn))

  const en = String(
    statement.en || statement.english || opts.enFallback || ''
  ).trim()
  return en ? mergeEnglishGameTokens(tokenizeEnglishForExpectedWords(en)) : []
}

/**
 * 判题用标准英文串：与输入槽同源（tokens > word_en > en），空格连接后再走 extractWords
 * @param {object|null|undefined} statement
 * @param {{ enFallback?: string }} [opts]
 * @returns {string}
 */
export function resolveExpectedAnswerText (statement, opts = {}) {
  const words = expectedWordsFromStatement(statement, opts)
  if (words.length) return words.join(' ')
  return String(statement?.en || statement?.english || opts.enFallback || '').trim()
}

/** structure_status：0 pending / 1 valid / 2 invalid / 3 review */
const STRUCTURE_PENDING = 0
const STRUCTURE_VALID = 1
const STRUCTURE_INVALID = 2
const STRUCTURE_REVIEW = 3

const TAG_LABEL_ORDER = [
  ['place', '地名'],
  ['person', '人名'],
  ['abbreviation', '缩写'],
  ['number', '数字'],
  ['proper_noun', '专有名词']
]

export function isQuestionV2TokensEnabled () {
  return USE_QUESTION_V2_TOKENS === true
}

/**
 * @param {unknown} raw
 * @returns {{ unit_type: number, unit_position: number, position: number, surface: string, tags: string[], is_direct_display: boolean, classification_source: string, classification_confidence: number }[]}
 */
export function normalizeTokenList (raw) {
  if (!Array.isArray(raw) || !raw.length) return []
  const list = []
  for (let i = 0; i < raw.length; i++) {
    const t = raw[i]
    if (!t || typeof t !== 'object') continue
    const surface = String(t.surface ?? '').trim()
    if (!surface) continue
    const position = Number(t.position)
    list.push({
      unit_type: Number(t.unit_type) || 0,
      unit_position: Number(t.unit_position) || 0,
      position: Number.isFinite(position) && position > 0 ? position : i + 1,
      surface,
      tags: Array.isArray(t.tags) ? t.tags.map((x) => String(x || '').trim()).filter(Boolean) : [],
      is_direct_display: t.is_direct_display === true || t.is_direct_display === 1,
      classification_source: String(t.classification_source || '').trim(),
      classification_confidence: Number(t.classification_confidence) || 0
    })
  }
  list.sort((a, b) => a.position - b.position || a.unit_position - b.unit_position)
  return list
}

/**
 * 解析当前题有效 tokens（开关关或空 → []，调用方保底旧逻辑）
 * @param {object|null|undefined} statement
 */
export function resolveQuestionTokens (statement) {
  if (!isQuestionV2TokensEnabled() || !statement) return []
  const own = normalizeTokenList(statement.tokens)
  if (own.length) return own
  if (Number(statement.is_original) === 1) {
    return normalizeTokenList(statement._parentQuestion?.tokens)
  }
  return []
}

export function shouldPreferQuestionTokens (statement) {
  return resolveQuestionTokens(statement).length > 0
}

/**
 * 朗读文本是否为 tokens 中标记的缩写（tags 含 abbreviation）
 * 用于 tts1：en 长度 > 1 时默认不传 phoneme，缩写例外仍传
 */
export function isAbbreviationSpeakText (statement, text) {
  const en = String(text || '').trim()
  if (!en || !statement) return false
  const tokens = resolveQuestionTokens(statement)
  if (!tokens.length) return false
  return tokens.some((t) => {
    if (!(t.tags || []).includes('abbreviation')) return false
    const surface = String(t.surface || '').trim()
    if (!surface) return false
    return tokensEqualForWordEnAlign(surface, en) || surface.toLowerCase() === en.toLowerCase()
  })
}

export function resolveStatementWordSlots (statement) {
  return resolveQuestionTokens(statement).map((t) => t.surface)
}

export function buildWordEnFromTokens (statement) {
  const slots = resolveStatementWordSlots(statement)
  return slots.length ? slots.join('|') : ''
}

export function resolveStatementWordEn (statement) {
  const fromTokens = buildWordEnFromTokens(statement)
  if (fromTokens) return fromTokens
  return String(statement?.word_en || '').trim()
}

function resolveStructureStatus (statement) {
  if (!statement) return null
  const raw = statement.structure_status
  if (raw === null || raw === undefined || raw === '') {
    return statement._parentQuestion != null
      ? resolveStructureStatus(statement._parentQuestion)
      : null
  }
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/** pending / invalid 不展示标签（仍可固定展示） */
export function shouldShowTokenTags (statement) {
  const status = resolveStructureStatus(statement)
  if (status === STRUCTURE_PENDING || status === STRUCTURE_INVALID) return false
  return true
}

export function isTokenTagMuted (token, statement) {
  if (!token) return false
  if (resolveStructureStatus(statement) === STRUCTURE_REVIEW) return true
  const conf = Number(token.classification_confidence)
  if (Number.isFinite(conf) && conf > 0 && conf < 0.7) return true
  return false
}

/**
 * 槽位小标签文案；非直接展示 / 无标签 / 不该展示时返回 ''
 * @returns {string}
 */
export function resolveTokenTagLabel (token, statement) {
  if (!token || !token.is_direct_display) return ''
  if (!shouldShowTokenTags(statement)) return ''
  const tags = token.tags || []
  if (!tags.length) return ''
  const conf = Number(token.classification_confidence)
  if (Number.isFinite(conf) && conf > 0 && conf < 0.7) return '待确认'
  for (let i = 0; i < TAG_LABEL_ORDER.length; i++) {
    const [key, label] = TAG_LABEL_ORDER[i]
    if (tags.includes(key)) return label
  }
  return ''
}

/** is_direct_display 的 surface + 标签元数据 */
export function getDirectDisplayTokenMetas (statement) {
  return resolveQuestionTokens(statement)
    .filter((t) => t.is_direct_display)
    .map((t) => ({
      surface: t.surface,
      tagLabel: resolveTokenTagLabel(t, statement),
      tagMuted: isTokenTagMuted(t, statement)
    }))
}

/** @deprecated 偏好 getDirectDisplayTokenMetas；保留表面列表兼容 */
export function getDirectDisplayTokenSurfaces (statement) {
  return getDirectDisplayTokenMetas(statement).map((m) => m.surface)
}

function normalizeDirectDisplayMetas (statementOrMetasOrSurfaces) {
  if (Array.isArray(statementOrMetasOrSurfaces)) {
    return statementOrMetasOrSurfaces
      .map((item) => {
        if (item && typeof item === 'object' && item.surface != null) {
          return {
            surface: String(item.surface || '').trim(),
            tagLabel: String(item.tagLabel || '').trim(),
            tagMuted: !!item.tagMuted
          }
        }
        const surface = String(item || '').trim()
        return surface ? { surface, tagLabel: '', tagMuted: false } : null
      })
      .filter((m) => m && m.surface)
  }
  return getDirectDisplayTokenMetas(statementOrMetasOrSurfaces)
}

/**
 * 在 expectedWords[start] 起是否匹配某直接展示 token
 * - 优先：surface 按空格分段（"New York"）
 * - 兜底：surface 按英文分词（"U.S." → U . S .），兼容未保持原子 surface 的旧展开
 * @returns {{ length: number, text: string, tagLabel: string, tagMuted: boolean } | null}
 */
export function matchDirectDisplayAt (expectedWords, startIndex, statementOrMetasOrSurfaces) {
  const metas = normalizeDirectDisplayMetas(statementOrMetasOrSurfaces)
  if (!metas.length || !Array.isArray(expectedWords)) return null
  const words = expectedWords
  const start = Number(startIndex) || 0
  if (start < 0 || start >= words.length) return null

  const tryMatchParts = (parts, surface, meta) => {
    if (!parts.length) return null
    if (start + parts.length > words.length) return null
    for (let k = 0; k < parts.length; k++) {
      if (!tokensEqualForWordEnAlign(words[start + k], parts[k])) return null
    }
    return {
      length: parts.length,
      text: parts.length === 1 ? String(words[start] || '').trim() || surface : surface,
      tagLabel: meta.tagLabel || '',
      tagMuted: !!meta.tagMuted
    }
  }

  const sorted = [...metas].sort((a, b) => b.surface.length - a.surface.length)
  for (let s = 0; s < sorted.length; s++) {
    const meta = sorted[s]
    const surface = meta.surface
    const spaceParts = surface.split(/\s+/).filter(Boolean)
    const hitSpace = tryMatchParts(spaceParts, surface, meta)
    if (hitSpace) return hitSpace

    // "U.S." 被拆成 U . S . 时仍能命中整段 fixedDisplay
    if (!/\s/.test(surface)) {
      const dottedParts = mergeEnglishGameTokens(tokenizeEnglishForExpectedWords(surface))
      if (dottedParts.length > 1) {
        const hitDotted = tryMatchParts(dottedParts, surface, meta)
        if (hitDotted) {
          return {
            ...hitDotted,
            text: surface
          }
        }
      }
    }
  }
  return null
}
